import React, { useState, useEffect, useCallback } from 'react';
import { Typography, Table, Button, Tag, Space, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { formatTS } from '../utils/time';
import api from '../api';

dayjs.extend(relativeTime);

export default function AlertsPage() {
  const navigate = useNavigate();
  const [alerts, setAlerts]   = useState([]);
  const [loading, setLoading] = useState(false);

  const unreadCount = alerts.filter((a) => !a.IsRead).length;

  const fetchAlerts = useCallback(() => {
    setLoading(true);
    api.get('/api/alerts')
      .then((res) => setAlerts(res.data))
      .catch(() => message.error('Failed to load notifications.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  const handleMarkRead = async (alert) => {
    if (!alert.IsRead) {
      await api.post(`/api/alerts/${alert.ID}/read`).catch(() => {});
      setAlerts((prev) => prev.map((a) => a.ID === alert.ID ? { ...a, IsRead: 1 } : a));
    }
    if (alert.LinkPath) {
      navigate(alert.LinkPath);
    }
  };

  const handleMarkAllRead = async () => {
    await api.post('/api/alerts/read-all').catch(() => {});
    setAlerts((prev) => prev.map((a) => ({ ...a, IsRead: 1 })));
  };

  const columns = [
    {
      title: '',
      key: 'status',
      width: 60,
      render: (_, r) => r.IsRead
        ? <Tag color="default" style={{ fontSize: 11 }}>Read</Tag>
        : <Tag color="blue"    style={{ fontSize: 11 }}>New</Tag>,
    },
    {
      title: 'Notification',
      dataIndex: 'Text',
      key: 'Text',
      render: (text, r) => (
        <Typography.Text strong={!r.IsRead}>{text}</Typography.Text>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'CreatedAt',
      key: 'CreatedAt',
      width: 180,
      render: (v) => (
        <Space direction="vertical" size={0}>
          <Typography.Text style={{ fontSize: 13 }}>
            {formatTS(v, 'MMM D, YYYY h:mm A z')}
          </Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            {dayjs.utc(v).fromNow()}
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: '',
      key: 'action',
      width: 100,
      render: (_, r) => r.LinkPath
        ? (
          <Button type="link" size="small" style={{ padding: 0 }} onClick={() => handleMarkRead(r)}>
            View →
          </Button>
        ) : null,
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Notifications
          {unreadCount > 0 && (
            <Typography.Text style={{ fontSize: 14, fontWeight: 400, marginLeft: 10, color: '#1677ff' }}>
              {unreadCount} unread
            </Typography.Text>
          )}
        </Typography.Title>
        {unreadCount > 0 && (
          <Button onClick={handleMarkAllRead}>Mark all as read</Button>
        )}
      </div>

      <Table
        rowKey="ID"
        dataSource={alerts}
        columns={columns}
        loading={loading}
        size="small"
        pagination={{ defaultPageSize: 25, showTotal: (t) => `${t} notifications` }}
        onRow={(r) => ({
          onClick: () => handleMarkRead(r),
          style: {
            cursor: r.LinkPath ? 'pointer' : 'default',
            background: r.IsRead ? undefined : '#e6f4ff',
          },
        })}
        locale={{ emptyText: 'No notifications.' }}
      />
    </>
  );
}
